import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Upload, Trash2, Loader2, Filter, X, RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { UploadDocumentoForm } from '@/components/Forms';
import { useCondomini } from '@/hooks/useCondomini';
import { useDocumenti } from '@/hooks/useDocumenti';
import { supabase } from '@/integrations/supabase/client';
import { ROUTE_PATHS, formatDate } from '@/lib/index';
import type { Documento } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface AnalysisState {
  docId: string;
  analisiId: string;
  startedAt: number;
  progress: number;
}

export default function Documenti() {
  const navigate = useNavigate();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedCondominio, setSelectedCondominio] = useState<string>('all');
  const [selectedTipo, setSelectedTipo] = useState<string>('all');
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [activeAnalysis, setActiveAnalysis] = useState<AnalysisState | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { condomini, loading: condominioLoading } = useCondomini();
  const { documenti, loading: documentiLoading, deleteDocumento, refetch } = useDocumenti(
    selectedCondominio === 'all' ? undefined : selectedCondominio
  );

  const condominioMap = new Map(condomini.map((c) => [c.id, c.nome]));

  const enrichedDocumenti: (Documento & { condominio_nome?: string })[] = documenti.map((doc) => ({
    ...doc,
    condominio_nome: condominioMap.get(doc.condominio_id),
  }));

  const filteredDocumenti = enrichedDocumenti.filter((doc) => {
    if (selectedTipo !== 'all' && doc.tipo !== selectedTipo) return false;
    return true;
  });

  useEffect(() => {
    if (!activeAnalysis) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    const poll = async () => {
      try {
        const { data } = await supabase
          .from('analisi')
          .select('stato, titolo')
          .eq('id', activeAnalysis.analisiId)
          .single();

        if (!data) return;

        const elapsed = (Date.now() - activeAnalysis.startedAt) / 1000;
        const simProgress = Math.min(90, Math.round((elapsed / 120) * 90));
        setActiveAnalysis((prev) => (prev ? { ...prev, progress: simProgress } : null));

        if (data.stato === 'completata') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          setActiveAnalysis(null);
          await refetch();
          toast.success('✅ Analisi completata! Visualizzala nella sezione Analisi.', { duration: 6000 });
          setTimeout(() => navigate(ROUTE_PATHS.ANALISI), 1500);
        } else if (data.stato === 'errore') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          setActiveAnalysis(null);
          await refetch();
          toast.error("❌ Errore durante l'analisi. Riprova.", { duration: 8000 });
        }
      } catch (err) {
        console.error('Errore polling:', err);
      }
    };

    pollingRef.current = setInterval(poll, 5000);
    poll();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [activeAnalysis?.analisiId, navigate, refetch]);

  const handleAnalyze = async (doc: Documento) => {
    if (activeAnalysis) {
      toast.warning('Analisi già in corso. Attendi il completamento.', { duration: 4000 });
      return;
    }

    const tipoComplementare = doc.tipo === 'consuntivo' ? 'preventivo' : 'consuntivo';
    const complementare = documenti.find(
      (d) => d.condominio_id === doc.condominio_id && d.anno === doc.anno && d.tipo === tipoComplementare
    );

    if (complementare) {
      toast.info(`Trovato anche il ${tipoComplementare} ${doc.anno}: analisi completa con entrambi i documenti`, {
        duration: 4000,
      });
    }

    try {
      const { error: updateError } = await supabase
        .from('documenti')
        .update({ stato: 'elaborazione' })
        .eq('id', doc.id);

      if (updateError) {
        throw new Error(`Errore aggiornamento stato: ${updateError.message}`);
      }

      await refetch();

      const { data, error: fnError } = await supabase.functions.invoke('analyze-document-ai-2026-05-08', {
        body: {
          documento_id: doc.id,
        },
      });

      if (fnError) {
        let msg = fnError.message || 'Errore avvio analisi';
        try {
          const ctx = (fnError as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const errBody = (await ctx.json().catch(() => ({}))) as Record<string, unknown>;
            if (errBody?.error) msg = String(errBody.error);
          }
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }

      if (data?.error) throw new Error(String(data.error));
      if (!data?.analisi_id) throw new Error('Risposta non valida dalla edge function');

      setActiveAnalysis({
        docId: doc.id,
        analisiId: data.analisi_id,
        startedAt: Date.now(),
        progress: 5,
      });

      toast.info('⏳ Analisi AI avviata. Elaborazione in corso...', { duration: 5000 });
      navigate(ROUTE_PATHS.ANALISI);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      toast.error(`Errore avvio analisi: ${msg}`, { duration: 8000 });
      await refetch();
    }
  };

  const handleDelete = async (documentoId: string) => {
    setDeletingDocId(documentoId);
    try {
      await deleteDocumento(documentoId);
      toast.success('Documento eliminato con successo');
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      toast.error(`Errore eliminazione: ${msg}`);
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleUploadSuccess = async () => {
    toast.success('Documento caricato con successo');
    await refetch();
    setUploadDialogOpen(false);
  };

  const clearFilters = () => {
    setSelectedCondominio('all');
    setSelectedTipo('all');
  };

  const hasActiveFilters = selectedCondominio !== 'all' || selectedTipo !== 'all';

  const getStatoBadge = (stato: string, docId: string) => {
    const isAnalyzing = activeAnalysis?.docId === docId;
    if (isAnalyzing) {
      return (
        <Badge className="gap-1 bg-primary/20 text-primary border border-primary/30">
          <Loader2 className="h-3 w-3 animate-spin" />
          Elaborazione
        </Badge>
      );
    }
    switch (stato) {
      case 'caricato':
        return <Badge variant="secondary">Caricato</Badge>;
      case 'elaborazione':
        return (
          <Badge className="gap-1 bg-primary/20 text-primary border border-primary/30">
            <Clock className="h-3 w-3" />
            Elaborazione
          </Badge>
        );
      case 'analizzato':
        return (
          <Badge className="gap-1 bg-success/20 text-success border border-success/30">
            <CheckCircle className="h-3 w-3" />
            Analizzato
          </Badge>
        );
      case 'errore':
        return (
          <Badge className="gap-1 bg-danger/20 text-danger border border-danger/30">
            <AlertCircle className="h-3 w-3" />
            Errore
          </Badge>
        );
      default:
        return <Badge variant="secondary">{stato}</Badge>;
    }
  };

  const getTipoBadgeClass = (tipo: string) => {
    return tipo === 'consuntivo' ? 'bg-primary text-primary-foreground' : 'bg-[#F59E0B] text-white';
  };

  return (
    <DashboardLayout title="Documenti">
      <div className="space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Documenti</h1>
            <p className="mt-2 text-muted-foreground">Gestisci i documenti PDF dei tuoi condomini</p>
          </div>
          <Button onClick={() => setUploadDialogOpen(true)} size="lg" className="gap-2 shrink-0">
            <Upload className="h-5 w-5" />
            Carica Documento
          </Button>
        </motion.div>

        <AnimatePresence>
          {activeAnalysis && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="rounded-xl border border-primary/30 bg-primary/10 p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <Loader2 className="h-5 w-5 animate-spin text-primary shrink-0" />
                <div>
                  <p className="font-medium text-primary">Analisi AI in corso...</p>
                  <p className="text-sm text-primary/80">
                    Claude sta leggendo il documento e generando l'analisi completa. Puoi navigare liberamente,
                    riceverai una notifica al termine.
                  </p>
                </div>
              </div>
              <Progress value={activeAnalysis.progress} className="h-2" />
              <p className="text-xs text-primary/60 mt-1">
                Tempo stimato: 1-3 minuti · {activeAnalysis.progress}% completato
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-sm"
        >
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-medium">Filtri</h2>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto gap-1 text-xs">
                <X className="h-3 w-3" /> Cancella filtri
              </Button>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Condominio</label>
              <Select value={selectedCondominio} onValueChange={setSelectedCondominio} disabled={condominioLoading}>
                <SelectTrigger>
                  <SelectValue placeholder="Tutti i condomini" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i condomini</SelectItem>
                  {condomini.map((condo) => (
                    <SelectItem key={condo.id} value={condo.id}>
                      {condo.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Tipo Documento</label>
              <Select value={selectedTipo} onValueChange={setSelectedTipo}>
                <SelectTrigger>
                  <SelectValue placeholder="Tutti i tipi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  <SelectItem value="consuntivo">Consuntivo</SelectItem>
                  <SelectItem value="preventivo">Preventivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </motion.div>

        {documentiLoading ? (
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="h-64 animate-pulse rounded-lg bg-muted" />
          </div>
        ) : filteredDocumenti.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-muted/30 py-24"
          >
            <FileText className="mb-4 h-16 w-16 text-muted-foreground" />
            <h3 className="mb-2 text-xl font-semibold text-foreground">
              {hasActiveFilters ? 'Nessun documento trovato' : 'Nessun documento caricato'}
            </h3>
            <p className="mb-6 text-center text-muted-foreground">
              {hasActiveFilters ? 'Prova a modificare i filtri' : 'Inizia caricando il tuo primo documento PDF'}
            </p>
            {!hasActiveFilters && (
              <Button onClick={() => setUploadDialogOpen(true)} size="lg" className="gap-2">
                <Upload className="h-5 w-5" /> Carica Documento
              </Button>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="rounded-xl border border-border bg-card shadow-sm"
          >
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome File</TableHead>
                    <TableHead>Condominio</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Anno</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Data Caricamento</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <AnimatePresence mode="popLayout">
                    {filteredDocumenti.map((doc) => {
                      const isThisAnalyzing = activeAnalysis?.docId === doc.id;
                      return (
                        <motion.tr
                          key={doc.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="group"
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <span className="truncate max-w-[200px]">{doc.nome_file}</span>
                            </div>
                          </TableCell>
                          <TableCell>{doc.condominio_nome || 'N/A'}</TableCell>
                          <TableCell>
                            <Badge className={getTipoBadgeClass(doc.tipo)}>
                              {doc.tipo === 'consuntivo' ? 'Consuntivo' : 'Preventivo'}
                            </Badge>
                          </TableCell>
                          <TableCell>{doc.anno}</TableCell>
                          <TableCell>{getStatoBadge(doc.stato, doc.id)}</TableCell>
                          <TableCell className="text-muted-foreground">{formatDate(doc.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleAnalyze(doc)}
                                disabled={!!activeAnalysis}
                                className="gap-1"
                                variant={doc.stato === 'analizzato' ? 'outline' : 'default'}
                                title={doc.stato === 'analizzato' ? 'Rianalizza' : 'Avvia analisi'}
                              >
                                {isThisAnalyzing ? (
                                  <>
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Analisi...
                                  </>
                                ) : doc.stato === 'analizzato' ? (
                                  <>
                                    <RefreshCw className="h-3 w-3" />
                                    Rianalizza
                                  </>
                                ) : (
                                  'Analizza'
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDelete(doc.id)}
                                disabled={deletingDocId === doc.id || isThisAnalyzing}
                              >
                                {deletingDocId === doc.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3 w-3" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </div>
          </motion.div>
        )}
      </div>

      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setUploadDialogOpen(true)}
        className="fixed bottom-8 right-8 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl"
      >
        <Upload className="h-6 w-6" />
      </motion.button>

      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <UploadDocumentoForm onSuccess={handleUploadSuccess} />
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
